import {
  type AtdOrderAssetClass,
  type AtdOrderDirection,
  type AtdOrderType,
  OMS_ORDER_STATUSES,
  type OmsOrderStatus,
} from "./api";

export type { OmsOrderStatus };
export { OMS_ORDER_STATUSES };

/** Raw OMS statuses omitted from the Advanced Filters Status picker. */
export const ADVANCED_FILTER_HIDDEN_OMS_STATUSES = [
  "stopped",
  "suspended",
  "calculated",
  "accepted_for_bidding",
  "pending_review",
  "done_for_day",
] as const satisfies readonly OmsOrderStatus[];

const ADVANCED_FILTER_HIDDEN_OMS_STATUS_SET = new Set<string>(
  ADVANCED_FILTER_HIDDEN_OMS_STATUSES,
);

/** Raw OMS statuses offered in the Advanced Filters Status multi-select. */
export const FILTERABLE_OMS_ORDER_STATUSES = OMS_ORDER_STATUSES.filter(
  (status) => !ADVANCED_FILTER_HIDDEN_OMS_STATUS_SET.has(status),
);

export const isFilterableOmsStatus = (
  status: string,
): status is (typeof FILTERABLE_OMS_ORDER_STATUSES)[number] =>
  (FILTERABLE_OMS_ORDER_STATUSES as readonly string[]).includes(status);

/** Aggregate buckets plus 1:1 OMS statuses for the Status filter. */
export type OrdersWidgetStatusFilter =
  | "all"
  | "open"
  | "closed"
  | OmsOrderStatus;

export type OrdersWidgetStatusSelection = readonly OrdersWidgetStatusFilter[];

export type OrdersWidgetOrderTypeFilter = "all" | AtdOrderType;
export type OrdersWidgetAssetClassFilter = "all" | AtdOrderAssetClass;
export const ORDERS_ROLLING_TIME_WINDOWS = [
  "last24Hours",
  "last3Days",
  "last7Days",
  "last1Month",
] as const;

export type OrdersWidgetRollingTimeWindow =
  (typeof ORDERS_ROLLING_TIME_WINDOWS)[number];

export type OrdersWidgetTimeWindow =
  | "all"
  | OrdersWidgetRollingTimeWindow
  | "custom";
export type OrdersWidgetBadgeColor =
  | "gray"
  | "green"
  | "orange"
  | "purple"
  | "red"
  | "yellow";

export interface OrdersWidgetLeg {
  id: string;
  sideLabel: string;
  sideColor: OrdersWidgetBadgeColor;
  description: string;
  ratio: string;
  quantity: string;
  filledLabel: string;
  positionIntent: string;
}

export const ORDERS_STATUS_TABS = ["all", "open", "filled", "closed"] as const;
export type OrdersWidgetStatusTab = (typeof ORDERS_STATUS_TABS)[number];

export interface OrdersWidgetFilters {
  symbol: string;
  status: OrdersWidgetStatusSelection;
  orderType: OrdersWidgetOrderTypeFilter;
  assetClass: OrdersWidgetAssetClassFilter;
  timeWindow: OrdersWidgetTimeWindow;
  fromDate: string;
  toDate: string;
  direction: AtdOrderDirection;
}

export interface OrdersWidgetRow {
  id: string;
  action: "Edit / Cancel";
  canEdit: boolean;
  editDisabledReason?: string;
  canCancel: boolean;
  cancelDisabledReason?: string;
  canReplace: boolean;
  isEditing: boolean;
  isCancelling: boolean;
  isLegRow: boolean;
  /** Truthful `ratio_qty` for a linked child row; empty on parent/single rows. */
  ratioLabel: string;
  parentOrderId: string;
  submittedAtValue: string;
  createdAtValue: string;
  startTimeValue: string;
  endTimeValue: string;
  createdAt: string;
  submittedAt: string;
  legCount: number;
  legs: readonly OrdersWidgetLeg[];
  status: string;
  statusColor: OrdersWidgetBadgeColor;
  symbol: string;
  rawSymbol: string;
  name: string;
  side: string;
  sideColor: OrdersWidgetBadgeColor;
  quantity: string;
  filledQuantity: string;
  orderType: string;
  limitPrice: string;
  stopPrice: string;
  trailPrice: string;
  trailPercent: string;
  extendedHours: string;
  clientOrderId: string;
  orderClass: string;
  algorithm: string;
  destination: string;
  startTime: string;
  endTime: string;
  maxPercentage: string;
  notional: string;
  timeInForce: string;
  filledAtAveragePrice: string;
  source: string;
  positionIntent: string;
  markets: string;
  currency: string;
}
